import {createStyles} from '@mantine/core';
import classNames from 'classnames';
import React from 'react';

import {
  REDESIGN_DRAWER_BORDER,
  REDESIGN_DRAWER_HORIZONTAL_PADDING_PX,
  REDESIGN_DRAWER_VERTICAL_PADDING_PX,
  REDESIGN_DRAWER_WIDTH_CSS_VAR,
  REDESIGN_DRAWER_WIDTH_PX,
} from 'llm/chat-library/ui/AIStyleConstants';

import type {SidepanelChat} from '../ChatRouter';
import ChatSidebarNav from './ChatSidebarNav';
import GroupedChatListing, {type ChatHistoryActions} from './GroupedChatListing';
import RedesignSidebarToolbar from './RedesignSidebarToolbar';

const useStyles = createStyles(() => ({
  root: {
    // Content width tracks the (resizable) drawer width minus its horizontal padding, so RECENT
    // labels reveal more as the user drags the drawer wider. The variable only changes on drag (not
    // during open/close), so this stays constant while the parent animates its width and clips via
    // overflow:hidden — no reflow during the reveal animation.
    width: `calc(var(${REDESIGN_DRAWER_WIDTH_CSS_VAR}, ${REDESIGN_DRAWER_WIDTH_PX}px) - ${
      2 * REDESIGN_DRAWER_HORIZONTAL_PADDING_PX
    }px)`,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  divider: {
    height: 1,
    flex: 'none',
    backgroundColor: REDESIGN_DRAWER_BORDER,
    // Negative horizontal margins span the drawer's padding so the rule reaches both edges.
    margin: `${REDESIGN_DRAWER_HORIZONTAL_PADDING_PX}px -${REDESIGN_DRAWER_HORIZONTAL_PADDING_PX}px 22px`,
  },
  listing: {
    overflowY: 'auto',
    // Pull the scrollbar into the drawer's horizontal padding (1px inset avoids clipping).
    marginRight: -REDESIGN_DRAWER_HORIZONTAL_PADDING_PX + 1,
    // Move drawer's botom padding inside the scroll container so we don't have a floating scrollbar.
    paddingBottom: REDESIGN_DRAWER_VERTICAL_PADDING_PX,
    marginBottom: -REDESIGN_DRAWER_VERTICAL_PADDING_PX,
  },
}));

export interface RedesignChatSidebarProps {
  selectedChatSession: SidepanelChat | null;
  onSelectChatSession: (chat: SidepanelChat) => void;
  /** Start a new chat (mirrors the header "New chat" action). */
  onNewChat: () => void;
  /** Whether the drawer clipping this sidebar is open; see {@link RedesignSidebarToolbarProps}. */
  isDrawerOpen: boolean;
  refetchRef: React.MutableRefObject<() => void>;
  historyActionsRef?: React.MutableRefObject<ChatHistoryActions | null>;
}

/**
 * Contents of the redesigned chat sidebar (LLM_ENABLE_CHAT_REDESIGN): a top toolbar (search +
 * settings), the primary navigation list, then the grouped (Starred / Recent) chat history.
 */
export default function RedesignChatSidebar({
  selectedChatSession,
  onSelectChatSession,
  onNewChat,
  isDrawerOpen,
  refetchRef,
  historyActionsRef,
}: RedesignChatSidebarProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classes.root}>
      <RedesignSidebarToolbar isDrawerOpen={isDrawerOpen} />
      <div className={classes.divider} />
      <ChatSidebarNav onNewChat={onNewChat} />
      <div className={classNames(classes.listing, 'scrollbar-override')}>
        <GroupedChatListing
          selectedChatSession={selectedChatSession}
          onSelectChatSession={onSelectChatSession}
          refetchRef={refetchRef}
          historyActionsRef={historyActionsRef}
          fillHeight={false}
        />
      </div>
    </div>
  );
}
